import * as z from 'zod'
import type { CommandAnchor } from '../../lib/agent-command-anchor.js'
import { baseEntitySchema } from '../entity.js'
import { coreElementValueSchema } from '../slate/slate-element.js'

/**
 * A task on a comment (GH #3041): `{}` while open; ticked, who ticked it and when. Anyone who can comment can tick it,
 * through the chat-actions endpoint, so it is never a client PATCH
 */
export const taskSchema = z.object({
  doneBy: z.string().describe('Who ticked the task done').optional(),
  doneAt: z.string().describe('When it was ticked done').optional(),
})

export type Task = z.infer<typeof taskSchema>

export const commentKinds = ['chat', 'nudge', 'proposal'] as const
export type CommentKind = (typeof commentKinds)[number]

export const commentSchema = baseEntitySchema.extend({
  type: z.literal('comment'),
  userId: z.string(),
  resolved: z.boolean().default(false).optional(),
  /**
   * What this comment IS: a chat turn, an agent nudge, or an agent proposal.
   * Optional (not defaulted) — legacy comments predate the field and are
   * recognized forever via the metadata-flag fallbacks in the guards below.
   * New writes stamp BOTH kind and the legacy flag during the deploy window.
   */
  kind: z.enum(commentKinds).describe('chat | nudge | proposal').optional(),
  children: coreElementValueSchema,
  task: taskSchema.describe('Set when the comment is a task (GH #3041)').optional(),
  formerTask: taskSchema
    .describe('The task as it was before "Not a task", so Undo restores its done state. Server-written')
    .optional(),
  likedBy: z
    .array(z.string())
    .describe('Who likes the comment now, in like order (GH #2796). Server-written')
    .optional(),
  /**
   * Everyone who ever liked the comment, so only a first like notifies. Read only by the server's like notification
   * generator; the UI never shows it (Brian, 2026-11-02)
   */
  likeHistory: z.array(z.string()).describe('Everyone who ever liked the comment. Server-written').optional(),
})

/** Comment fields only the server writes, through the chat-actions endpoint: a PATCH or a create drops them */
export const serverOwnedCommentFields = ['task', 'formerTask', 'likedBy', 'likeHistory'] as const

export type Comment = z.infer<typeof commentSchema>

export function isComment(node: unknown): node is Comment {
  return commentSchema.safeParse(node).success
}

const metadataFlag = (comment: Comment, flag: string): boolean =>
  typeof comment.metadata === 'object' &&
  comment.metadata !== null &&
  (comment.metadata as Record<string, unknown>)[flag] === true

/** A task not yet ticked done */
export function isOpenTask(comment: Comment): boolean {
  return Boolean(comment.task) && !comment.task?.doneBy
}

export function isProposalComment(comment: Comment): boolean {
  return comment.kind === 'proposal' || metadataFlag(comment, 'agentProposal')
}

/**
 * Whether a row is the agent's words: written by 'assistant', or an agent proposal whoever owns the row.
 * Proposals written before GH #2451 are owned by the user who asked; reading the kind as well covers those
 * without migrating them. Takes a loose shape so chat items and raw store rows both fit.
 */
export const isAgentAuthoredComment = (row: { userId?: string; kind?: string; metadata?: unknown }): boolean =>
  row.userId === 'assistant' || isProposalComment(row as Comment)

export function isNudgeComment(comment: Comment): boolean {
  return comment.kind === 'nudge' || metadataFlag(comment, 'agentNudge')
}

export function isChatComment(comment: Comment): boolean {
  return !isProposalComment(comment) && !isNudgeComment(comment)
}

export interface EditorCommandProposalInput {
  /** The agent the proposal is a child of */
  agentId: string
  /** The invoking user, granted WRITE so they can accept or reject; authorship is `assistant` */
  userId: string
  /** The entity the command edits, e.g. the song */
  targetEntityId: string
  summary: string
  commandId: string
  args?: Record<string, unknown>
  anchor: CommandAnchor
  /** The target's editors (`entityEditors`), who may see and resolve the proposal too (GH #2677 §8) */
  editors?: string[]
  /** The exchange the proposal belongs to (GH #2677) */
  exchange?: Exchange
}

/**
 * Who owns an agent proposal, and who may act on it (GH #2451). One source for every proposal the agent
 * server writes and for the E2E seed.
 *
 * - Owned by 'assistant', like the chat-reply Comment: the chat feed and the context assembler read
 *   authorship from `userId === 'assistant'`. The `create` call must pass 'assistant' too, because it
 *   stamps its own `userId` argument on the entity, whatever the partial says.
 * - The invoking user gets WRITE, not READ: accepting or rejecting stamps `metadata.resolution` with a
 *   client PATCH, which the graph update handler allows only with WRITE. That is no more than the user
 *   could do when these were (wrongly) owned by the user. The ACL principal is also how push delivers it.
 */
export const proposalOwnership = (invokingUserId: string, editors: string[] = []) => ({
  userId: 'assistant',
  // GH #2677 §8: a suggested edit is visible to, and resolvable by, every editor of the target (not only the asker)
  acls: [...new Set([invokingUserId, ...editors])].map(principal => ({ principal, permission: 'WRITE' as const })),
})

const pseudoPrincipals = new Set(['PUBLIC', 'AUTHENTICATED'])

/**
 * Who may edit an entity: its owner plus every principal granted WRITE or DELETE, minus the PUBLIC and
 * AUTHENTICATED pseudo-principals and expired grants. Used to share a proposal with every editor of its target
 * (GH #2677 §8). Read at creation, so an editor added later does not see older proposals (a known limit).
 */
export const entityEditors = (
  entity: { userId?: string; acls?: { principal: string; permission: string; expiresAt?: string }[] } | null,
  now: Date = new Date()
): string[] => {
  if (!entity) {
    return []
  }
  const granted = (entity.acls ?? [])
    .filter(acl => acl.permission === 'WRITE' || acl.permission === 'DELETE')
    .filter(acl => !pseudoPrincipals.has(acl.principal))
    .filter(acl => !acl.expiresAt || new Date(acl.expiresAt) > now)
    .map(acl => acl.principal)
  return [...new Set([...(entity.userId ? [entity.userId] : []), ...granted])]
}

/**
 * One agent exchange (GH #2677): a question, its replies ("Try again" adds siblings) and any proposals, stamped
 * as `metadata.exchange` on each comment. The anchor is where the question was asked, so anchored threads can
 * group the exchange at its element.
 */
export const exchangeSchema = z.object({
  id: z.string().min(1).describe('Client-minted exchange id, reused by "Try again"'),
  kind: z
    .enum(['review'])
    .describe('A proactive review (GH #3099): the agent spoke after edits, nobody asked, so there is no question')
    .optional(),
  askedBy: z
    .string()
    .describe('User id of the asker, so other editors can see who a proposal was for without the asker-only question')
    .optional(),
  anchor: z
    .object({
      elementId: z.string().min(1).describe('The element the question was asked about'),
      containerId: z
        .string()
        .describe('The top-level block (line or section) holding the element, for when the element is gone')
        .optional(),
      entityId: z.string().describe('The entity holding the element').optional(),
      elementType: z.string().describe("The element's node type, e.g. 'chord', 'line', 'section'").optional(),
      label: z.string().describe('Display text for the element, e.g. the chord name; never a lookup key').optional(),
    })
    .describe('Where the question was asked')
    .optional(),
  discardedAt: z
    .string()
    .describe("When the asker discarded the exchange (stamped on the asker's question comment)")
    .optional(),
  discardedBy: z.string().describe('Who discarded the exchange').optional(),
})
export type Exchange = z.infer<typeof exchangeSchema>

/** The exchange for a turn, anchored at its focus when it has one, naming the asker when known */
export const exchangeFromFocus = (
  id: string,
  focus: { id: string; type?: string; entityId?: string; summary?: string; containerId?: string } | undefined,
  { askedBy }: { askedBy?: string } = {}
): Exchange => ({
  id,
  ...(askedBy ? { askedBy } : {}),
  ...(focus
    ? {
        anchor: {
          ...(focus.entityId ? { entityId: focus.entityId } : {}),
          elementId: focus.id,
          ...(focus.containerId ? { containerId: focus.containerId } : {}),
          ...(focus.type ? { elementType: focus.type } : {}),
          ...(focus.summary ? { label: focus.summary } : {}),
        },
      }
    : {}),
})

/**
 * Where a block comment was written (GH #2795), stamped on create by the client, which is the only side that can name
 * the block: a block thread's chat hangs off the editor element, not the document. Notifications read it as a snapshot.
 */
export const commentAnchorSchema = z.object({
  entityId: z.string().min(1).describe('The document the comment was written in, e.g. the song'),
  elementId: z.string().min(1).describe('The block the thread is on'),
  label: z
    .string()
    .describe('How the thread named the block when the comment was written, e.g. Verse 2, or a lyric line in quotes')
    .optional(),
})
export type CommentAnchor = z.infer<typeof commentAnchorSchema>

/** A comment's stamped anchor, or undefined when it has none (written before the stamp, or malformed) */
export const getCommentAnchor = (comment: { metadata?: unknown }): CommentAnchor | undefined => {
  const parsed = commentAnchorSchema.safeParse((comment.metadata as { anchor?: unknown } | undefined)?.anchor)
  return parsed.success ? parsed.data : undefined
}

/** A comment's exchange, or undefined when it has none (or a malformed one) */
export const getExchange = (comment: { metadata?: unknown }): Exchange | undefined => {
  const raw = (comment.metadata as { exchange?: unknown } | undefined)?.exchange
  const parsed = exchangeSchema.safeParse(raw)
  return raw !== undefined && parsed.success ? parsed.data : undefined
}

/**
 * The partial entity an editor-command proposal is persisted as: what the agent server writes when a
 * tool proposes a command (`propose_chord_change`, `run_editor_command`), and what E2E seeds, so a
 * seeded proposal is the same thing a real agent turn produces (GH #2352).
 *
 * Ownership and access per `proposalOwnership`. `kind` and the legacy `metadata.agentProposal` flag are
 * both stamped for the deploy window. `anchorBlockId` lets the gutter chip find the proposal without
 * reading the patch.
 */
export const editorCommandProposalEntity = (input: EditorCommandProposalInput) => ({
  type: 'comment' as const,
  parentId: input.agentId,
  ...proposalOwnership(input.userId, input.editors),
  kind: 'proposal' as const,
  children: [{ type: 'line' as const, children: [{ text: `[proposal] ${input.summary}` }] }],
  metadata: {
    agentProposal: true,
    targetEntityId: input.targetEntityId,
    summary: input.summary,
    anchorBlockId: input.anchor.id,
    blockSpan: 1,
    patch: {
      kind: 'editor-command' as const,
      commandId: input.commandId,
      args: input.args,
      anchor: input.anchor,
    },
    ...(input.exchange ? { exchange: input.exchange } : {}),
  },
})
